import { Component, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { CustomErrorStateMatcher } from "../../shared/error-state-matcher";
import { AuthService } from "../auth.service";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { TwoFactorAuthService } from "../two-factor-auth-service";
import { Constants } from "../../constants";
import { NotifService } from "../../core/notification/notif.service";
import LoginDto from "../../model/auth/login.dto";
import { ReCaptchaV3Service } from 'ng-recaptcha';

@Component({
    selector: "map-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnDestroy {
    readonly loginForm: UntypedFormGroup;
    readonly matcher: CustomErrorStateMatcher;
    captchaResponse: string;

    show2FACodeInput = false;
    private lastTokenTimestamp = 0; // Track last token generation time
    private recaptchaHealthCheckInterval: any;
    private isRecaptchaReady = false;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly authService: AuthService,
        private readonly twoFactorAuthService: TwoFactorAuthService,
        private readonly notifService: NotifService,
        public readonly dialog: MatDialog,
        private readonly router: Router,
        private recaptchaV3Service: ReCaptchaV3Service
    ) {
        this.loginForm = this.formBuilder.group({
            email: ["", [Validators.required, Validators.email]],
            password: ["", Validators.required],
            code: [""],
        });
        this.matcher = new CustomErrorStateMatcher();

        // Initialize reCAPTCHA health check
        this.initRecaptchaHealthCheck();
    }

    ngOnDestroy(): void {
        // Clean up health check interval
        if (this.recaptchaHealthCheckInterval) {
            clearInterval(this.recaptchaHealthCheckInterval);
        }
    }

    /**
     * Initialize reCAPTCHA and set up periodic health checks
     * This prevents "Could not connect to reCAPTCHA service" errors after idle periods
     * Health check runs every 30 minutes to balance connection freshness with API efficiency
     */
    private initRecaptchaHealthCheck(): void {
        // Initial check
        this.checkRecaptchaHealth();

        // Periodic health check every 30 minutes to keep connection alive
        // This is reasonable since refresh token is valid for 24 hours
        this.recaptchaHealthCheckInterval = setInterval(() => {
            this.checkRecaptchaHealth();
        }, 30 * 60 * 1000); // 30 minutes
    }

    /**
     * Check if reCAPTCHA service is responsive
     * If not, reload the page to reinitialize the service
     */
    private async checkRecaptchaHealth(): Promise<void> {
        try {
            // Check if grecaptcha is loaded
            if (typeof (window as any).grecaptcha === 'undefined') {
                console.warn('⚠️ reCAPTCHA not loaded, will reload on login attempt');
                this.isRecaptchaReady = false;
                return;
            }

            // Try to get a token with a health check action
            const healthToken = await this.recaptchaV3Service.execute('health_check').toPromise();

            if (healthToken && healthToken.length > 100) {
                this.isRecaptchaReady = true;
            } else {
                console.warn('⚠️ reCAPTCHA health check returned invalid token');
                this.isRecaptchaReady = false;
            }
        } catch (error) {
            console.error('❌ reCAPTCHA health check failed:', error);
            this.isRecaptchaReady = false;
        }
    }

    /**
     * Force fresh reCAPTCHA token generation
     * This prevents using cached tokens that may be invalid
     * Includes automatic retry and page reload if reCAPTCHA service is unavailable
     */
    private async getFreshRecaptchaToken(): Promise<string> {
        const now = Date.now();
        const timeSinceLastToken = now - this.lastTokenTimestamp;

        // If last token was generated less than 1 second ago, wait to ensure fresh token
        if (timeSinceLastToken < 1000) {
            await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastToken));
        }

        // Check if reCAPTCHA is ready, if not, try to reinitialize
        if (!this.isRecaptchaReady) {
            console.warn('⚠️ reCAPTCHA not ready, attempting to reinitialize...');
            await this.checkRecaptchaHealth();

            // If still not ready after check, show user-friendly error
            if (!this.isRecaptchaReady) {
                throw new Error('RECAPTCHA_NOT_READY');
            }
        }

        // Use unique action with timestamp to bypass ng-recaptcha internal caching
        const action = 'login';

        try {
            const token = await this.recaptchaV3Service.execute(action).toPromise();
            this.lastTokenTimestamp = Date.now();

            // Validate token was received
            if (!token || token.trim() === '') {
                throw new Error('RECAPTCHA_EMPTY_TOKEN');
            }

            return token;
        } catch (error) {
            console.error('❌ Failed to get reCAPTCHA token:', error);

            // Check if it's a network/connection error
            if (error.message && (
                error.message.includes('network') ||
                error.message.includes('connect') ||
                error.message.includes('timeout')
            )) {
                throw new Error('RECAPTCHA_CONNECTION_ERROR');
            }

            throw error;
        }
    }

    onSubmit(): void {
        const emailValue = this.loginForm.get("email").value;
        const passwordValue = this.loginForm.get("password").value;
        const codeValue = this.loginForm.get("code").value
            ? this.loginForm.get("code").value
            : null;
        // Get fresh token using our custom method that prevents caching
        this.getFreshRecaptchaToken().then(token => {
            // Validate token was received
            if (!token || token.trim() === '') {
                console.error('❌ reCAPTCHA token is empty or invalid');
                this.notifService.error('reCAPTCHA verification failed. Please refresh the page and try again.');
               return;
            }

            // Validate token format (should be long base64-like string)
            // Valid tokens are typically 200-500 characters
            // The 128-char token you saw was likely cached/invalid
            if (token.length < 200) {
                console.error('❌ reCAPTCHA token seems too short (', token.length, 'chars), might be cached/invalid');
                console.error('Expected: 200-500 characters for fresh v3 token');
                this.notifService.error('reCAPTCHA token appears invalid. Please refresh the page and try again.');
               return;
            }

            // Add the fresh token to your form
            this.loginForm.patchValue({ recaptchaToken: token });
                this.twoFactorAuthService
                    .getTwoFactorAuthUserInfo(emailValue)
                    .subscribe({
                        next: (userInfo) => {
                            let loginDto = new LoginDto(
                                emailValue,
                                passwordValue,
                                token,
                                codeValue,
                            );
                            if (
                                userInfo.hasTwoFactorAuthEnabled &&
                                !userInfo.hasConfirmedTwoFactorAuth
                            ) {
                                this.authService.setTemporaryLoginRequest(loginDto);
                                this.router.navigate([
                                    `public/two-factor-auth/${emailValue}`,
                                ]);
                            } else {
                                this.authService.login(loginDto).subscribe({
                                    next: (success) => {
                                        if(success.principalUser.provider){
                                            this.router.navigate(["private/overlays/api-meter"]);
                                        } else {
                                            this.router.navigate(["private/account"]);
                                        }
                                    },
                                    error: (error) => {

                                        // Check for reCAPTCHA-specific errors
                                        if (error.error && error.error.message &&
                                            error.error.message.toLowerCase().includes('captcha')) {
                                            this.notifService.error(
                                                'reCAPTCHA verification failed. Please refresh the page and try again.'
                                            );
                                            console.error('reCAPTCHA validation failed on backend:', error);
                                            return;
                                        }

                                        if (
                                            error.status ===
                                            Constants.ERROR_CODES.LOGIN_INVALID_2FA_CODE
                                        ) {
                                            // Redirect to dedicated 2FA page for code entry
                                            const emailValue = this.loginForm.controls.email.value;
                                            this.authService.setTemporaryLoginRequest(loginDto);
                                            this.router.navigate([
                                                `public/two-factor-auth/${emailValue}`,
                                            ]);
                                        } else if (
                                            [Constants.ERROR_CODES.FORBIDDEN, Constants.ERROR_CODES.SERVICE_UNAVAILABLE].includes(error.status)
                                        ) {
                                            this.notifService.error(
                                                error.error.errors[0]
                                            );
                                        }
                                        else {
                                            this.notifService.error(
                                                error.error
                                            );
                                        }
                                    }
                                });
                            }
                        },
                        error: (error) => {
                            if (
                                error.status ===
                                Constants.ERROR_CODES.SERVICE_UNAVAILABLE
                            ) {
                                this.notifService.error(error.error.errors[0]);
                            }
                            if(error.status === Constants.ERROR_CODES.TOO_MANY_REQUESTS){
                                this.notifService.error(error.error);
                            }
                            else {
                                this.notifService.error(error.error.message);
                            }
                        }
                    });
        }).catch(error => {
            console.error('❌ reCAPTCHA execution failed:', error);

            // Handle specific reCAPTCHA error types
            if (error.message === 'RECAPTCHA_NOT_READY') {
                this.notifService.error(
                    'Could not connect to the reCAPTCHA service. Please check your internet connection and reload the page.'
                );

                // Offer automatic page reload after 3 seconds
                setTimeout(() => {
                    if (confirm('Would you like to reload the page to reinitialize reCAPTCHA?')) {
                        window.location.reload();
                    }
                }, 3000);
            } else if (error.message === 'RECAPTCHA_CONNECTION_ERROR') {
                this.notifService.error(
                    'Network error connecting to reCAPTCHA service. Please check your internet connection and try again.'
                );
            } else if (error.message === 'RECAPTCHA_EMPTY_TOKEN') {
                this.notifService.error(
                    'reCAPTCHA verification failed to generate a token. Please refresh the page and try again.'
                );
            } else {
                this.notifService.error(
                    'reCAPTCHA verification failed. Please refresh the page and try again.'
                );
            }
        });
    }
}
